/**
 * CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 -- the ONE payment-handoff
 * derivation (HANDOFF LAW).
 *
 * Pure. Derives the front-desk handoff state of a visit from fields the
 * board read already ships (or ships as of this package) -- NEVER a status
 * write, never an invented value. Consumed by the day board's ops chips +
 * Collect CTA, the receptionist Today tile, and the doctor queue-context
 * drawer, so every surface speaks the SAME vocabulary:
 *
 *   - collect      -> "Payment pending" / the Collect CTA with the suggested
 *                     whole-INR sum (doctor-accepted plan, unpaid).
 *   - advised_only -> "Follow-up needed" / "No payment handoff - advised
 *                     only" (closed visit, nothing accepted, something
 *                     advised).
 *   - recorded     -> "Payment recorded" (a non-voided payment links the
 *                     appointment -- evidence, not a book of record).
 *   - none         -> no handoff applies (no verified pre-close due, or no plan signals).
 *
 * CLOSED includes the R-1 UNGRANTED branch: a doctor's close without the
 * fulfil grant locks the sheet (visitStatus 'finalized') while the stored
 * status stays checked_in -- the front desk must still see the handoff, so
 * the derivation treats a finalized visit as closed regardless of status.
 */

export interface PaymentHandoffInput {
  readonly status: string;
  readonly visitStatus?: 'none' | 'draft' | 'finalized' | undefined;
  readonly paymentRecorded?: boolean | undefined;
  readonly acceptedPlanCount?: number | undefined;
  readonly advisedPlanCount?: number | undefined;
  /** Whole INR (sum of the accepted plan estimates); null when no accepted
   *  item carries a value. */
  readonly suggestedCollectAmount?: number | null | undefined;
  /** RECEPTION-MONEY-TRUTH-V1 (A2/A3): the episode's NETTED outstanding in
   *  minor units -- THE ONE server derivation's number, present ONLY on the
   *  MONEY-AUDIENCE wire (structurally absent for the doctor, so a
   *  non-money surface keeps today's behavior byte-identical). When a
   *  POSITIVE outstanding is known, a part-paid visit KEEPS its
   *  collect door (the A1 walk's R4 break: any payment used to erase the
   *  door while Rs 1,000 was still owed on the floor). */
  readonly outstandingMinorUnits?: number | null | undefined;
}

export type PaymentHandoffState =
  | { readonly kind: 'recorded' }
  | { readonly kind: 'collect'; readonly amount: number | null }
  | { readonly kind: 'advised_only' }
  | { readonly kind: 'none' };

/** The visit is CLOSED for handoff purposes: the stored status reached a
 *  done state, OR the clinical record is finalized (the R-1 ungranted
 *  close -- the doctor's part is done, the front desk completes). */
export function visitClosedForHandoff(row: PaymentHandoffInput): boolean {
  return (
    row.status === 'fulfilled' ||
    row.status === 'completed' ||
    row.visitStatus === 'finalized'
  );
}

/** Terminal-NEGATIVE stored statuses: a cancelled / no-show row never
 *  carries the Collect CTA (adversarial finding F7 -- a cancelled visit's
 *  collection is a billing/human question, not a board shout; recorded
 *  payment evidence still shows). */
const HANDOFF_SUPPRESSED_STATUSES: readonly string[] = ['cancelled', 'no_show'];

/** Derive the handoff state (see module header for the vocabulary).
 *
 * RECEPTION-MONEY-TRUTH-V1 (A2/A3 -- the part-payment door law): on a
 * MONEY-AUDIENCE row (outstandingMinorUnits PRESENT), a closed,
 * non-terminal visit with a POSITIVE netted outstanding stays 'collect'
 * even after a payment -- the door offers the REMAINDER (whole rupees
 * only; a paise-bearing outstanding offers the door with no prefill --
 * money is never rounded to fit a prefill). 'recorded' then means what it
 * says: paid AND nothing outstanding. A wire WITHOUT the money field (the
 * doctor's, and every pre-A2 payload) keeps the original behavior
 * byte-identical -- the audience gate does the role-shaping structurally. */
export function derivePaymentHandoff(
  row: PaymentHandoffInput,
): PaymentHandoffState {
  const nettedOutstanding =
    typeof row.outstandingMinorUnits === 'number' &&
    Number.isFinite(row.outstandingMinorUnits) &&
    row.outstandingMinorUnits > 0
      ? row.outstandingMinorUnits
      : null;
  if (row.paymentRecorded === true && nettedOutstanding === null) {
    return { kind: 'recorded' };
  }
  if (HANDOFF_SUPPRESSED_STATUSES.includes(row.status)) {
    // Terminal-negative rows: recorded evidence still shows; no collect
    // shout either way (the pre-existing F7 posture, unchanged).
    return row.paymentRecorded === true ? { kind: 'recorded' } : { kind: 'none' };
  }
  if (!visitClosedForHandoff(row)) {
    // D14: the server supplies only this visit's verified remainder to the
    // money audience. Accepted estimates or carried balances alone cannot
    // open collection before close. Preserve paise without rounding a prefill.
    if (nettedOutstanding !== null) {
      return { kind: 'collect', amount: nettedOutstanding % 100 === 0 ? nettedOutstanding / 100 : null };
    }
    return row.paymentRecorded === true ? { kind: 'recorded' } : { kind: 'none' };
  }
  const accepted = row.acceptedPlanCount ?? 0;
  const advised = row.advisedPlanCount ?? 0;
  if (row.paymentRecorded === true && nettedOutstanding !== null) {
    // The part-paid closed visit: the door survives and speaks the
    // REMAINDER (the netted truth), never the gross estimate again.
    return {
      kind: 'collect',
      amount:
        nettedOutstanding % 100 === 0 ? nettedOutstanding / 100 : null,
    };
  }
  if (accepted > 0) {
    return {
      kind: 'collect',
      amount:
        nettedOutstanding !== null && nettedOutstanding % 100 === 0
          ? nettedOutstanding / 100
          : typeof row.suggestedCollectAmount === 'number' &&
              Number.isFinite(row.suggestedCollectAmount) &&
              row.suggestedCollectAmount > 0
            ? row.suggestedCollectAmount
            : null,
    };
  }
  if (advised > 0) return { kind: 'advised_only' };
  return { kind: 'none' };
}

/** The receptionist Today glance: how many visits await collection + the
 *  total suggested sum (whole INR; null when no eligible row carries an
 *  estimate). Pure -- derived from the SAME board rows (one computation). */
export interface CollectQueueSummary {
  readonly count: number;
  readonly totalAmount: number | null;
}

export function collectQueueSummary(
  rows: readonly PaymentHandoffInput[],
): CollectQueueSummary {
  let count = 0;
  let totalAmount: number | null = null;
  let allAmountsKnown = true;
  for (const row of rows) {
    const state = derivePaymentHandoff(row);
    if (state.kind !== 'collect') continue;
    count += 1;
    if (state.amount === null) allAmountsKnown = false;
    else totalAmount = (totalAmount ?? 0) + state.amount;
  }
  // A subset sum is not the queue total. Show the visit count until every
  // eligible episode has a trustworthy amount.
  return { count, totalAmount: allAmountsKnown ? totalAmount : null };
}

/** The advised-only line (HANDOFF LAW copy; a quiet line, never a button). */
export const NO_HANDOFF_ADVISED_ONLY_COPY = 'No payment handoff - advised only';

/** The Collect CTA label (HANDOFF LAW copy). Whole INR, en-IN grouping. */
export function collectCtaLabel(amount: number | null): string {
  return amount === null
    ? 'Collect payment'
    : `Collect payment - Rs ${amount.toLocaleString('en-IN')} suggested`;
}

/** The CTA qualifier (title/sub copy) -- states the provenance honestly.
 *  Adversarial finding F4: never the bare word "advised" here -- one row
 *  below it means the OPPOSITE state ("No payment handoff - advised only"),
 *  so the qualifier names the ACCEPTED plan explicitly. */
export function collectCtaQualifier(amount: number | null): string {
  return amount === null
    ? 'Doctor accepted - no estimate recorded'
    : "From the doctor's accepted plan";
}
